import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Money from 'components/Money/Money';
import utilsMessages from '../../../utils/messages';
import FreeShippingIcon from './FreeShippingIcon';
import MoqIcon from './MoqIcon';
import { ExclamationCircleFilled } from '@ant-design/icons';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  gap: ${({ gap }) => gap || '0px'};
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #111827;
`;

const Bold = styled.span`
  font-weight: 600;
  color: #111827;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const ProgressContainer = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  background: #eaebea;
  border-radius: 8px;
  overflow: hidden;
`;
const MoqFlag = styled.div`
  display: ${({ noDisplay }) => (noDisplay ? 'none' : 'block')};
  position: absolute;
  left: ${({ left }) => left};
  top: 0;
  width: 1px;
  height: 3px;
  background: #e2483c;
`;
const ProgressValue = styled.div`
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  max-width: 100%;
  width: ${({ width }) => width};
  height: 3px;
  background: ${({ color }) => color};
  transition: width 0.4s;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 20px;
`;

interface IDeliveryFeeSection {
  deliveryFee: any;
  subTotal: number;
  cartMoq: number;
}

const CartTotalProgressSection = (props: IDeliveryFeeSection) => {
  const { deliveryFee, subTotal, cartMoq = 0 } = props;

  const [progressWidth, setProgressWidth] = useState(0);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const deliveryFeeProgressRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();

    if (!node) return;

    setProgressWidth(node.offsetWidth);

    resizeObserverRef.current = new ResizeObserver(([entry]) => {
      setProgressWidth(entry.contentRect.width);
    });

    resizeObserverRef.current.observe(node);
  }, []);

  const isLessThanCartMOQ = useMemo(() => (!!subTotal ? subTotal < cartMoq : true), [cartMoq, subTotal]);

  const isCanReachFreeDelivery = useMemo(
    () => !isLessThanCartMOQ && (deliveryFee?.isFreeDelivery || deliveryFee?.remainingToFree > 0),
    [deliveryFee, isLessThanCartMOQ],
  );

  const deliveryFeeStyles = useMemo(() => {
    if (!progressWidth || !subTotal || !deliveryFee?.fee) {
      return {
        width: 0,
        progressBackground: '#F38A3E',
      };
    }

    const destinationMoq =
      (!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0) || cartMoq > deliveryFee?.deliveryFeeThreshold
        ? cartMoq
        : deliveryFee?.deliveryFeeThreshold;

    if (!destinationMoq) {
      return {
        width: progressWidth,
        progressBackground: '#6FC373',
      };
    }

    const width = (subTotal / destinationMoq) * progressWidth;

    if (isLessThanCartMOQ) {
      return {
        width: Math.min(width, progressWidth),
        progressBackground: '#E2483C',
      };
    }

    if (deliveryFee?.isFreeDelivery || deliveryFee?.remainingToFree <= 0) {
      return {
        width: progressWidth,
        progressBackground: '#6FC373',
      };
    }

    return {
      width: Math.min(width, progressWidth),
      progressBackground: '#F38A3E',
    };
  }, [subTotal, isLessThanCartMOQ, deliveryFee, cartMoq, isCanReachFreeDelivery, progressWidth]);

  const TitleComponent = useCallback(() => {
    return (
      <Title>
        {isLessThanCartMOQ ? (
          <>
            <ExclamationCircleFilled
              style={{ color: '#E2483C', fontSize: '16px', verticalAlign: 'middle', position: 'relative', top: '-1px' }}
            />
            {` `}
            MOQ: <Money>{cartMoq}</Money>
            {`. `}
            <FormattedMessage {...utilsMessages.addMoreProducts} />
            {` `}
            <Bold>
              <Money>{cartMoq - subTotal}</Money>
            </Bold>
            {` `}
            <FormattedMessage {...utilsMessages.canOrder} />
          </>
        ) : !deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0 ? (
          <>
            <FormattedMessage {...utilsMessages.youCanPlaceAnOrder} />
          </>
        ) : deliveryFee?.isFreeDelivery ? (
          <>
            <FormattedMessage {...utilsMessages.freeShippingTitle} />
            {` `}
            <Bold>
              <FormattedMessage {...utilsMessages.freeShippingContent} />
            </Bold>
          </>
        ) : (
          <>
            <FormattedMessage {...utilsMessages.addMoreProducts} />
            {` `}
            <Bold>
              <Money>{deliveryFee?.remainingToFree}</Money>
            </Bold>
            {` `}
            <FormattedMessage {...utilsMessages.toGetFreeService} />
          </>
        )}
      </Title>
    );
  }, [subTotal, deliveryFee, isLessThanCartMOQ, cartMoq]);

  return (
    <Wrapper gap={isCanReachFreeDelivery || isLessThanCartMOQ ? '0px' : '8px'}>
      {TitleComponent()}
      <ProgressWrapper>
        <ProgressContainer ref={deliveryFeeProgressRef}>
          <MoqFlag
            left={`${(cartMoq / deliveryFee?.deliveryFeeThreshold) * progressWidth}px`}
            noDisplay={
              !cartMoq ||
              cartMoq >= deliveryFee?.deliveryFeeThreshold ||
              (!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0)
            }
          />
          <ProgressValue width={`${deliveryFeeStyles.width}px`} color={deliveryFeeStyles.progressBackground} />
        </ProgressContainer>
        {isCanReachFreeDelivery && (
          <IconWrapper>
            <FreeShippingIcon />
          </IconWrapper>
        )}

        {isLessThanCartMOQ && (
          <IconWrapper>
            <MoqIcon />
          </IconWrapper>
        )}
      </ProgressWrapper>
    </Wrapper>
  );
};

export default CartTotalProgressSection;
